import { CampaignStatus } from './CampaignStatus';
import { CommunicationChannel } from './communications';
import { JourneyStatus } from './journeys';
import { CommonReservedTags } from './tags';

export enum FieldTypes {
    STRING = 'string',
    NUMBER = 'number',
    BOOLEAN = 'boolean',
    DATETIME = 'datetime',
    LOOKUP_ENUM = 'lookup_enum',
    ARRAY = 'array',
}

export enum ArrayFilterOperators {
    IS = 'is',
    IS_NOT = 'is_not',
    IS_EMPTY = 'is_empty',
    IS_NOT_EMPTY = 'is_not_empty',
}

export enum StringFilterOperators {
    IS = 'is',
    IS_NOT = 'is_not',
    CONTAINS = 'contains',
    NOT_CONTAINS = 'not_contains',
    STARTS_WITH = 'starts_with',
    ENDS_WITH = 'ends_with',
    IS_EMPTY = 'is_empty',
    IS_NOT_EMPTY = 'is_not_empty',
}

export enum NumberFilterOperators {
    IS = 'is',
    IS_NOT = 'is_not',
    GREATER_THAN = 'greater_than',
    LESS_THAN = 'less_than',
    GREATER_THAN_OR_EQUAL_TO = 'greater_than_or_equal_to',
    LESS_THAN_OR_EQUAL_TO = 'less_than_or_equal_to',
    IS_EMPTY = 'is_empty',
    IS_NOT_EMPTY = 'is_not_empty',
}

export enum BooleanFilterOperators {
    IS = 'is',
    IS_NOT = 'is_not',
    IS_EMPTY = 'is_empty',
    IS_NOT_EMPTY = 'is_not_empty',
}

export enum DateTimeFilterOperators {
    IS = 'is',
    IS_BEFORE = 'is_before',
    IS_AFTER = 'is_after',
    IS_ON_OR_BEFORE = 'is_on_or_before',
    IS_ON_OR_AFTER = 'is_on_or_after',
    IS_BETWEEN = 'is_between',
    IS_EMPTY = 'is_empty',
    IS_NOT_EMPTY = 'is_not_empty',
}

export enum LookupEnumFilterOperators {
    IS = 'is',
    IS_NOT = 'is_not',
    IS_EMPTY = 'is_empty',
    IS_NOT_EMPTY = 'is_not_empty',
}

export type AnyFilterOperators =
    | StringFilterOperators
    | NumberFilterOperators
    | BooleanFilterOperators
    | DateTimeFilterOperators
    | LookupEnumFilterOperators
    | ArrayFilterOperators;

export enum SrtEntities {
    CAMPAIGNS = 'campaigns',
    AUDIENCES = 'audiences',
    JOURNEYS = 'journeys',
    EVENTS = 'events',
    TEMPLATES_EMAIL = 'templates_email',
    TEMPLATES_SMS = 'templates_sms',
    TEMPLATES_PUSH = 'templates_push',
    TEMPLATES_WHATSAPP = 'templates_whatsapp',
    MEDIA_LIBRARY = 'media_library',
}

export const getTemplateEntityForChannel = (
    channel: CommunicationChannel,
): SrtEntities => {
    switch (channel) {
        case CommunicationChannel.EMAIL:
            return SrtEntities.TEMPLATES_EMAIL;
        case CommunicationChannel.SMS:
            return SrtEntities.TEMPLATES_SMS;
        case CommunicationChannel.PUSH:
            return SrtEntities.TEMPLATES_PUSH;
        case CommunicationChannel.WHATSAPP:
            return SrtEntities.TEMPLATES_WHATSAPP;
        default:
            return SrtEntities.TEMPLATES_EMAIL;
    }
};

export type StringFilter = {
    fieldType: FieldTypes.STRING;
    field: string;
    operator: StringFilterOperators;
    value: string[];
};

export type NumberFilter = {
    fieldType: FieldTypes.NUMBER;
    field: string;
    operator: NumberFilterOperators;
    value: number[];
};

export type BooleanFilter = {
    fieldType: FieldTypes.BOOLEAN;
    field: string;
    operator: BooleanFilterOperators;
    value: boolean[];
};

export type DateTimeFilter = {
    fieldType: FieldTypes.DATETIME;
    field: string;
    operator: DateTimeFilterOperators;
    value: Date[];
};

export type LookupEnumFilter = {
    fieldType: FieldTypes.LOOKUP_ENUM;
    field: string;
    operator: LookupEnumFilterOperators;
    value: string[];
};

export type ArrayFilter = {
    fieldType: FieldTypes.ARRAY;
    field: string;
    operator: ArrayFilterOperators;
    value: string[];
};

export type AnyFilter =
    | StringFilter
    | NumberFilter
    | BooleanFilter
    | DateTimeFilter
    | LookupEnumFilter
    | ArrayFilter;

export type View = {
    projectUuid: string;
    id: string;
    name: string;
    description?: string;
    isDeleted?: boolean;
    filters: {
        value: AnyFilter[];
    };
    entity: SrtEntities;
    createdAt: Date;
    updatedAt?: Date;
    createdBy: string;
    updatedBy?: string;
};

export type CreateViewRequest = Pick<
    View,
    'name' | 'description' | 'filters' | 'entity'
>;
export type UpdateViewRequest = Partial<View>;

export enum SrtCampaignViewIds {
    DRAFTS = 'srt_campaign_drafts',
    ACTIVE = 'srt_campaign_active',
    COMPLETED = 'srt_campaign_completed',
    FAILED = 'srt_campaign_failed',
    HIDDEN = 'srt_campaign_hidden',
    SCHEDULED = 'srt_campaign_scheduled',
    RUNNING = 'srt_campaign_running',
}

export enum SrtJourneyViewIds {
    DRAFTS = 'srt_journey_drafts',
    SCHEDULED = 'srt_journey_scheduled',
    ACTIVE = 'srt_journey_active',
    SUNSET = 'srt_journey_sunset',
    CANCELED = 'srt_journey_canceled',
    HIDDEN = 'srt_journey_hidden',
}

export enum SrtEventViewIds {
    HIDDEN = 'srt_events_hidden',
}

/** Internal (non-persisted) template manager views — one id per template entity. */
export enum SrtTemplateViewIds {
    EMAIL_HIDDEN = 'srt_templates_email_hidden',
    SMS_HIDDEN = 'srt_templates_sms_hidden',
    PUSH_HIDDEN = 'srt_templates_push_hidden',
}

const createTemplateHiddenSystemView = (
    id: SrtTemplateViewIds,
    entity:
        | SrtEntities.TEMPLATES_EMAIL
        | SrtEntities.TEMPLATES_SMS
        | SrtEntities.TEMPLATES_PUSH,
): View => ({
    name: 'Hidden',
    description: '',
    filters: {
        value: [
            {
                fieldType: FieldTypes.ARRAY,
                field: 'tags',
                operator: ArrayFilterOperators.IS,
                value: [CommonReservedTags.HIDDEN],
            },
        ],
    },
    id,
    entity,
    createdAt: new Date(),
    createdBy: 'system',
    projectUuid: '',
});

/** System template views resolved in TemplateService (same pattern as SrtCampaignViews). */
export const SrtTemplateInternalViews: View[] = [
    createTemplateHiddenSystemView(
        SrtTemplateViewIds.EMAIL_HIDDEN,
        SrtEntities.TEMPLATES_EMAIL,
    ),
    createTemplateHiddenSystemView(
        SrtTemplateViewIds.SMS_HIDDEN,
        SrtEntities.TEMPLATES_SMS,
    ),
    createTemplateHiddenSystemView(
        SrtTemplateViewIds.PUSH_HIDDEN,
        SrtEntities.TEMPLATES_PUSH,
    ),
];

export const getSrtTemplateDefaultViews = (entity: SrtEntities): View[] =>
    SrtTemplateInternalViews.filter((view) => view.entity === entity);

export const isSrtTemplateHiddenViewId = (viewId: string | null): boolean =>
    viewId !== null &&
    SrtTemplateInternalViews.some((view) => view.id === viewId);
// DEFAULT system views
export const SrtCampaignViews: View[] = [
    {
        name: 'Drafts',
        description: '',
        filters: {
            value: [
                {
                    fieldType: FieldTypes.LOOKUP_ENUM,
                    field: 'status',
                    operator: LookupEnumFilterOperators.IS,
                    value: [CampaignStatus.DRAFT],
                },
            ],
        },
        projectUuid: '',
        id: SrtCampaignViewIds.DRAFTS,
        entity: SrtEntities.CAMPAIGNS,
        createdAt: new Date(),
        createdBy: 'system',
    },
    {
        name: 'Active',
        description: '',
        filters: {
            value: [
                {
                    fieldType: FieldTypes.LOOKUP_ENUM,
                    field: 'status',
                    operator: LookupEnumFilterOperators.IS,
                    value: [
                        CampaignStatus.IN_PROGRESS,
                        CampaignStatus.SCHEDULED,
                        CampaignStatus.SENDING,
                        CampaignStatus.TRACKING_CONVERSIONS,
                        CampaignStatus.PREPARING_TO_SEND,
                        CampaignStatus.AWAITING_USER_INPUT,
                        CampaignStatus.READY,
                    ],
                },
            ],
        },
        id: SrtCampaignViewIds.ACTIVE,
        entity: SrtEntities.CAMPAIGNS,
        createdAt: new Date(),
        createdBy: 'system',
        projectUuid: '',
    },
    {
        name: 'Scheduled',
        description: '',
        filters: {
            value: [
                {
                    fieldType: FieldTypes.LOOKUP_ENUM,
                    field: 'status',
                    operator: LookupEnumFilterOperators.IS,
                    value: [CampaignStatus.SCHEDULED],
                },
            ],
        },
        id: SrtCampaignViewIds.SCHEDULED,
        entity: SrtEntities.CAMPAIGNS,
        createdAt: new Date(),
        createdBy: 'system',
        projectUuid: '',
    },
    {
        name: 'Running',
        description: '',
        filters: {
            value: [
                {
                    fieldType: FieldTypes.LOOKUP_ENUM,
                    field: 'status',
                    operator: LookupEnumFilterOperators.IS,
                    value: [
                        CampaignStatus.IN_PROGRESS,
                        CampaignStatus.SENDING,
                        CampaignStatus.PREPARING_TO_SEND,
                    ],
                },
            ],
        },
        id: SrtCampaignViewIds.RUNNING,
        entity: SrtEntities.CAMPAIGNS,
        createdAt: new Date(),
        createdBy: 'system',
        projectUuid: '',
    },
    {
        name: 'Completed',
        description: '',
        filters: {
            value: [
                {
                    fieldType: FieldTypes.LOOKUP_ENUM,
                    field: 'status',
                    operator: LookupEnumFilterOperators.IS,
                    value: [CampaignStatus.COMPLETED],
                },
            ],
        },
        id: SrtCampaignViewIds.COMPLETED,
        entity: SrtEntities.CAMPAIGNS,
        createdAt: new Date(),
        createdBy: 'system',
        projectUuid: '',
    },
    {
        name: 'Failed',
        description: '',
        filters: {
            value: [
                {
                    fieldType: FieldTypes.LOOKUP_ENUM,
                    field: 'status',
                    operator: LookupEnumFilterOperators.IS,
                    value: [CampaignStatus.FAILURE],
                },
            ],
        },
        id: SrtCampaignViewIds.FAILED,
        entity: SrtEntities.CAMPAIGNS,
        createdAt: new Date(),
        createdBy: 'system',
        projectUuid: '',
    },
    {
        name: 'Hidden',
        description: '',
        filters: {
            value: [
                {
                    fieldType: FieldTypes.ARRAY,
                    field: 'tags',
                    operator: ArrayFilterOperators.IS,
                    value: [CommonReservedTags.HIDDEN],
                },
            ],
        },
        id: SrtCampaignViewIds.HIDDEN,
        entity: SrtEntities.CAMPAIGNS,
        createdAt: new Date(),
        createdBy: 'system',
        projectUuid: '',
    },
];

// DEFAULT system views for Journeys
export const SrtJourneyViews: View[] = [
    {
        name: 'Drafts',
        description: '',
        filters: {
            value: [
                {
                    fieldType: FieldTypes.LOOKUP_ENUM,
                    field: 'status',
                    operator: LookupEnumFilterOperators.IS,
                    value: [JourneyStatus.DRAFT],
                },
            ],
        },
        projectUuid: '',
        id: SrtJourneyViewIds.DRAFTS,
        entity: SrtEntities.JOURNEYS,
        createdAt: new Date(),
        createdBy: 'system',
    },
    {
        name: 'Scheduled',
        description: '',
        filters: {
            value: [
                {
                    fieldType: FieldTypes.LOOKUP_ENUM,
                    field: 'status',
                    operator: LookupEnumFilterOperators.IS,
                    value: [JourneyStatus.SCHEDULED],
                },
            ],
        },
        id: SrtJourneyViewIds.SCHEDULED,
        entity: SrtEntities.JOURNEYS,
        createdAt: new Date(),
        createdBy: 'system',
        projectUuid: '',
    },
    {
        name: 'Active',
        description: '',
        filters: {
            value: [
                {
                    fieldType: FieldTypes.LOOKUP_ENUM,
                    field: 'status',
                    operator: LookupEnumFilterOperators.IS,
                    value: [JourneyStatus.ACTIVE],
                },
            ],
        },
        id: SrtJourneyViewIds.ACTIVE,
        entity: SrtEntities.JOURNEYS,
        createdAt: new Date(),
        createdBy: 'system',
        projectUuid: '',
    },
    {
        name: 'Sunset',
        description: '',
        filters: {
            value: [
                {
                    fieldType: FieldTypes.LOOKUP_ENUM,
                    field: 'status',
                    operator: LookupEnumFilterOperators.IS,
                    value: [JourneyStatus.ACTIVE],
                },
            ],
        },
        id: SrtJourneyViewIds.SUNSET,
        entity: SrtEntities.JOURNEYS,
        createdAt: new Date(),
        createdBy: 'system',
        projectUuid: '',
    },
    {
        name: 'Stopped',
        description: '',
        filters: {
            value: [
                {
                    fieldType: FieldTypes.LOOKUP_ENUM,
                    field: 'status',
                    operator: LookupEnumFilterOperators.IS,
                    value: [JourneyStatus.CANCELED],
                },
            ],
        },
        id: SrtJourneyViewIds.CANCELED,
        entity: SrtEntities.JOURNEYS,
        createdAt: new Date(),
        createdBy: 'system',
        projectUuid: '',
    },
    {
        name: 'Hidden',
        description: '',
        filters: {
            value: [
                {
                    fieldType: FieldTypes.ARRAY,
                    field: 'tags',
                    operator: ArrayFilterOperators.IS,
                    value: [CommonReservedTags.HIDDEN],
                },
            ],
        },
        id: SrtJourneyViewIds.HIDDEN,
        entity: SrtEntities.JOURNEYS,
        createdAt: new Date(),
        createdBy: 'system',
        projectUuid: '',
    },
];

export const SrtEventViews: View[] = [
    {
        name: 'Hidden',
        description: '',
        filters: {
            value: [
                {
                    fieldType: FieldTypes.ARRAY,
                    field: 'tags',
                    operator: ArrayFilterOperators.IS,
                    value: [CommonReservedTags.HIDDEN],
                },
            ],
        },
        id: SrtEventViewIds.HIDDEN,
        entity: SrtEntities.EVENTS,
        createdAt: new Date(),
        createdBy: 'system',
        projectUuid: '',
    },
];

export const isSrtEventHiddenViewId = (viewId: string | null): boolean =>
    viewId !== null && viewId === SrtEventViewIds.HIDDEN;

export enum SrtMediaLibraryViewIds {
    LARGE_FILES = 'srt_media_library_large_files',
    RECENTLY_ADDED = 'srt_media_library_recently_added',
    HIDDEN = 'srt_media_library_hidden',
}

export const SrtMediaLibraryViews: View[] = [
    {
        name: 'Large files',
        description: '',
        filters: {
            value: [
                {
                    fieldType: FieldTypes.NUMBER,
                    field: 'size',
                    operator: NumberFilterOperators.GREATER_THAN_OR_EQUAL_TO,
                    value: [512000],
                },
            ],
        },
        id: SrtMediaLibraryViewIds.LARGE_FILES,
        entity: SrtEntities.MEDIA_LIBRARY,
        createdAt: new Date(),
        createdBy: 'system',
        projectUuid: '',
    },
    {
        name: 'Recently added',
        description: '',
        filters: {
            value: [],
        },
        id: SrtMediaLibraryViewIds.RECENTLY_ADDED,
        entity: SrtEntities.MEDIA_LIBRARY,
        createdAt: new Date(),
        createdBy: 'system',
        projectUuid: '',
    },
    {
        name: 'Hidden',
        description: '',
        filters: {
            value: [
                {
                    fieldType: FieldTypes.BOOLEAN,
                    field: 'isHidden',
                    operator: BooleanFilterOperators.IS,
                    value: [true],
                },
            ],
        },
        id: SrtMediaLibraryViewIds.HIDDEN,
        entity: SrtEntities.MEDIA_LIBRARY,
        createdAt: new Date(),
        createdBy: 'system',
        projectUuid: '',
    },
];
